import BrandLogo from '../components/BrandLogo';
/*
 * src/pages/Login.tsx
 *
 * This file defines the Login page component.
 * It provides functionality for users to:
 * - Log in using email and password.
 * - Request a password reset if they've forgotten their password.
 * - Reset their password if they have a recovery token (typically from an email link).
 * The component interacts with the `useUserStore` for authentication.
 * It also includes a section displaying information about the application.
 */
import React, { useRef, useState } from 'react';
import {
  Eye, EyeOff, Mail, Lock, ArrowLeft, ArrowUpRight, Check, KeyRound,
  Radio, Dumbbell, ClipboardCheck, UsersRound,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildMagicLoginCallbackUrl, resolvePostLoginPath } from '../utils/loginNavigation';
import { toast } from 'react-toastify';
import { useUserStore } from '../store/userStore';
import { useI18n, Locale } from '../i18n';
import usePageView from '../hooks/usePageView';
import { trackEvent } from '../utils/analytics';
import SubtleFooter from '../components/SubtleFooter';
import { getPasswordChecks, isStrongPassword } from '../lib/passwordValidation';
import { requestOtpLogin, requestPasswordReset, verifyPasswordReset } from '../lib/authApi';
import BootstrapRecovery from '../components/BootstrapRecovery';
import { PasswordLoginAttempts } from '../lib/loginCooldown';

/**
 * The Login page component.
 * Handles user authentication (login, forgot password, reset password).
 * It presents a form for email/password input and manages different modes
 * for login, password recovery request, and password reset execution.
 * On successful login, the user is redirected to the main application.
 * Includes an informational section about the doHockey application.
 */
const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [passwordAttempts] = useState(() => {
    try { return new PasswordLoginAttempts(window.sessionStorage); }
    catch { return new PasswordLoginAttempts(); }
  });
  const [, refreshCooldown] = React.useReducer((value: number) => value + 1, 0);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [mode, setMode] = useState<'login' | 'forgot' | 'reset' | 'verify'>('login');
  const [resetFlow, setResetFlow] = useState<'reset' | 'activation'>('reset');
  const [loginMethod, setLoginMethod] = useState<'password' | 'magic'>('password');
  const [magicCodeSent, setMagicCodeSent] = useState(false);
  const callbackAttempted = useRef(false);
  const passwordWait = passwordAttempts.remainingSeconds(email);
  const passwordPaused = mode === 'login' && loginMethod === 'password' && passwordWait > 0;
  const waitLabel = `${Math.floor(passwordWait / 60)}:${String(passwordWait % 60).padStart(2, '0')}`;
  React.useEffect(() => {
    if (!passwordPaused) return;
    const timer = window.setInterval(refreshCooldown, 1000);
    return () => window.clearInterval(timer);
  }, [passwordPaused]);
  const { login, loginWithMagicCode } = useUserStore();
  const { t, locale, setLocale } = useI18n();
  usePageView('login', { page: 'login' });
  const navigate = useNavigate();
  const location = useLocation();
  const loginParams = new URLSearchParams(location.search);
  const passwordChecks = getPasswordChecks(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const publicDestinations = [
    { to: '/live/watch', icon: Radio, label: t('login_public_live') },
    { to: '/training-library', icon: Dumbbell, label: t('login_public_training') },
    { to: '/evaluation-access', icon: ClipboardCheck, label: t('login_public_evaluation') },
    { to: '/intake', icon: UsersRound, label: t('login_public_intake') },
  ];

  const finishSuccessfulLogin = React.useCallback(() => {
    const currentUser = useUserStore.getState().currentUser;
    const displayName = currentUser?.displayName?.trim();
    toast.success(
      displayName
        ? t('login_success_toast_with_name', { name: displayName })
        : t('login_success_toast')
    );
    navigate(resolvePostLoginPath(location.pathname, location.search, location.hash), { replace: true });
  }, [location.hash, location.pathname, location.search, navigate, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'login' && loginMethod === 'password' && passwordAttempts.remainingSeconds(email) > 0) return;
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      if (mode === 'login') {
        if (!email || (loginMethod === 'password' && !password)) {
          setError(t('login_error_required'));
          return;
        }

        if (loginMethod === 'magic') {
          if (!magicCodeSent) {
            trackEvent('magic_login_request_clicked');
            const redirectTo = buildMagicLoginCallbackUrl(window.location.origin);
            await requestOtpLogin(email, redirectTo);
            setMagicCodeSent(true);
            setSuccess(t('magic_login_sent', { email }));
          } else {
            if (!verificationCode.trim()) {
              setError(t('magic_login_code_required'));
              return;
            }
            trackEvent('magic_login_verify_clicked');
            const result = await loginWithMagicCode(email, verificationCode);
            if (!result.success) setError(result.message || t('magic_login_invalid'));
            else finishSuccessfulLogin();
          }
        } else {
          trackEvent('login_submit_clicked');
          if (password.length < 6) {
            setError(t('login_error_length'));
            return;
          }
          const result = await login(email, password);
          if (!result.success && result.code === 'AUTH_ACTIVATION_REQUIRED') {
            setResetFlow('activation');
            setSuccess(t('login_activation_required'));
            setPassword('');
          } else if (!result.success) {
            if (result.code === 'AUTH_LOGIN_LOCKED') {
              passwordAttempts.recordServerWait(email, result.retryAfterSeconds ?? 900);
              refreshCooldown();
            } else if (result.code === 'AUTH_INVALID_CREDENTIALS') {
              passwordAttempts.recordFailure(email);
              refreshCooldown();
            }
            setError(result.message || t('login_error_invalid'));
          } else {
            passwordAttempts.clear(email);
            finishSuccessfulLogin();
          }
        }
      } else if (mode === 'forgot') {
        if (!email) {
          setError(t('forgot_error_email_required'));
          return;
        }

        trackEvent('forgot_password_submit_clicked');
        try {
          await requestPasswordReset(email, locale);

          setSuccess(t('forgot_success_send'));
          setResetFlow('reset');
          setMode('verify'); // Switch to verification code input mode
        } catch (err) {
          setError(err instanceof Error ? err.message : t('forgot_error_send'));
        }
      } else if (mode === 'verify') {
        if (!verificationCode) {
          setError(t('verify_error_required'));
          return;
        }

        if (!newPassword || !confirmPassword) {
          setError(t('reset_error_required'));
          return;
        }

        if (!isStrongPassword(newPassword)) {
          setError(t('reset_error_length'));
          return;
        }

        if (newPassword !== confirmPassword) {
          setError(t('reset_error_mismatch'));
          return;
        }

        trackEvent('verify_code_submit_clicked');
        try {
          const verifyResponse = await verifyPasswordReset(email, verificationCode, newPassword);

          passwordAttempts.clear(email);
          refreshCooldown();
          const wasPendingActivation =
            Boolean(verifyResponse.was_pending_activation);

          setSuccess(wasPendingActivation ? t('activation_success') : t('verify_success'));
          setVerificationCode('');
          setNewPassword('');
          setConfirmPassword('');
          setResetFlow(wasPendingActivation ? 'activation' : 'reset');
          setMode('reset');
        } catch (err) {
          setError(err instanceof Error ? err.message : t('verify_error_invalid'));
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login_error_generic'));
    } finally {
      setIsLoading(false);
    }
  };

  // Check if we're in password reset mode from URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const modeParam = urlParams.get('mode');
    const flowParam = urlParams.get('flow');
    const emailParam = urlParams.get('email');
    const noticeParam = urlParams.get('notice');
    const codeParam = urlParams.get('code');

    if (emailParam) {
      setEmail(emailParam);
    }
    if (codeParam) {
      setVerificationCode(codeParam);
    }

    if (flowParam === 'activation') {
      setResetFlow('activation');
    } else if (flowParam === 'reset') {
      setResetFlow('reset');
    }

    if (noticeParam === 'code_sent') {
      setSuccess(
        flowParam === 'activation'
          ? t('activation_code_sent')
          : t('forgot_success_send')
      );
    }

    if (modeParam === 'verify') {
      setMode('verify');
      return;
    }

    if (urlParams.get('type') === 'recovery') {
      setMode('reset');
    }
  }, [t]);

  React.useEffect(() => {
    if (!location.pathname.endsWith('/auth/verify') || callbackAttempted.current) return;
    const callbackParams = new URLSearchParams(location.search);
    const callbackEmail = callbackParams.get('email')?.trim() || '';
    const callbackCode = callbackParams.get('code')?.trim() || '';
    setLoginMethod('magic');
    setMagicCodeSent(true);
    setEmail(callbackEmail);
    setVerificationCode(callbackCode);

    if (!callbackEmail || !callbackCode) {
      setError(t('magic_login_invalid'));
      return;
    }

    callbackAttempted.current = true;
    setError('');
    setSuccess(t('magic_login_verifying'));
    setIsLoading(true);
    void loginWithMagicCode(callbackEmail, callbackCode)
      .then((result) => {
        if (!result.success) {
          setSuccess('');
          setError(result.message || t('magic_login_invalid'));
          return;
        }
        finishSuccessfulLogin();
      })
      .catch((err) => {
        setSuccess('');
        setError(err instanceof Error ? err.message : t('magic_login_invalid'));
      })
      .finally(() => setIsLoading(false));
  }, [finishSuccessfulLogin, location.pathname, location.search, loginWithMagicCode, t]);

  return (
    <div className="flex min-h-screen flex-col bg-[#f7f8f8]">
      <header className="flex min-h-16 items-center justify-end border-b border-gray-200 bg-white px-4 md:px-8">
        <select
          aria-label={t('language')}
          value={locale}
          onChange={e => setLocale(e.target.value as Locale)}
          className="min-h-10 border-0 border-b-2 border-transparent bg-transparent px-2 text-sm font-semibold text-gray-600 focus:border-primary-600 focus:outline-none"
        >
          <option value="nl">🇳🇱 NL</option>
          <option value="en">🇬🇧 EN</option>
        </select>
      </header>
      <main className="mx-auto flex w-full max-w-5xl flex-grow items-center px-0 py-0 md:px-6 md:py-8">
        <div className="w-full border-y border-gray-200 bg-white md:grid md:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] md:divide-x md:divide-gray-200">
          <aside className="hidden flex-col p-8 md:flex lg:p-10" aria-label={t('login_intro_eyebrow')}>
            <BrandLogo className="self-start" imageClassName="h-11 max-w-full" />

            <div className="mt-8 max-w-sm">
              <p className="ui-kicker">{t('login_intro_eyebrow')}</p>
              <h2 className="ui-section-title mt-2">{t('login_intro_title')}</h2>
              <p className="mt-3 text-sm leading-6 text-gray-500">{t('login_intro_description')}</p>
              <div className="mt-5 border-y border-gray-200 py-3">
                <p className="ui-meta">{t('login_intro_beta')}</p>
              </div>
            </div>

            <nav className="mt-8 max-w-sm" aria-label={t('login_public_navigation')}>
              <div className="mb-2">
                <p className="ui-kicker">{t('login_public_eyebrow')}</p>
              </div>
              <div className="divide-y divide-gray-200 border-y border-gray-200">
                {publicDestinations.map(({ to, icon: Icon, label }) => (
                  <Link
                    key={to}
                    to={to}
                    className="group flex min-h-11 items-center gap-2 py-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center text-primary-600 transition-colors group-hover:bg-orange-50" aria-hidden="true">
                      <Icon size={16} />
                    </span>
                    <strong className="min-w-0 flex-1 text-xs font-semibold text-gray-700 group-hover:text-primary-700">{label}</strong>
                  </Link>
                ))}
              </div>
            </nav>
          </aside>

          <div className="mx-auto w-full max-w-xl space-y-6 p-6 sm:p-8 md:p-8 lg:p-10">
            <div className="flex items-center md:hidden">
              <BrandLogo imageClassName="h-12 max-w-full" />
            </div>

            {mode === 'login' && (
              <>
                <BootstrapRecovery />
                <div className="space-y-2 border-b border-gray-200 pb-6">
                  <p className="ui-kicker">{t('login_form_eyebrow')}</p>
                  <h1 className="ui-page-title">{t('login_title')}</h1>
                  {t('login_subtitle') !== 'login_subtitle' && (
                    <p className="text-sm text-gray-500">{t('login_subtitle')}</p>
                  )}
                  {loginParams.get('reason') === 'session-expired' && (
                    <p className="mt-3 border-l-2 border-primary-500 pl-3 text-sm text-gray-600">
                      {t('login_session_expired')}
                    </p>
                  )}
                </div>

                <form key="login-form" onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-2 border border-gray-200 bg-gray-50 p-1" aria-label={t('login_method_label')}>
                    <button
                      type="button"
                      aria-pressed={loginMethod === 'password'}
                      onClick={() => {
                        setLoginMethod('password');
                        setMagicCodeSent(false);
                        setVerificationCode('');
                        setError('');
                        setSuccess('');
                      }}
                      className={`min-h-11 px-3 text-sm font-semibold ${loginMethod === 'password' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                    >
                      {t('login_method_password')}
                    </button>
                    <button
                      type="button"
                      aria-pressed={loginMethod === 'magic'}
                      onClick={() => {
                        setLoginMethod('magic');
                        setPassword('');
                        setError('');
                        setSuccess('');
                      }}
                      className={`min-h-11 px-3 text-sm font-semibold ${loginMethod === 'magic' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                    >
                      {t('login_method_magic')}
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('login_email')}
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type="email"
                        id="login-email"
                        name="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="min-h-12 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 pl-10 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder={t('login_email_placeholder')}
                        disabled={isLoading}
                        required
                        readOnly={loginMethod === 'magic' && magicCodeSent}
                      />
                    </div>
                  </div>

                  {loginMethod === 'password' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('login_password')}
                      </label>
                      <div className="relative">
                        <Lock size={18} className="absolute left-3 top-3.5 text-gray-400" />
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="login-password"
                          name="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="min-h-12 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 pl-10 pr-10 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder={t('login_password_placeholder')}
                          aria-describedby={passwordPaused ? 'password-login-wait' : undefined}
                          disabled={isLoading || passwordPaused}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={t(showPassword ? 'login_hide_password' : 'login_show_password')}
                          className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                  ) : magicCodeSent ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('magic_login_code')}
                      </label>
                      <div className="relative">
                        <KeyRound size={18} className="absolute left-3 top-3.5 text-gray-400" />
                        <input
                          type="text"
                          inputMode="numeric"
                          id="magic-code"
                          name="magic_code"
                          autoComplete="one-time-code"
                          value={verificationCode}
                          onChange={(event) => setVerificationCode(event.target.value)}
                          className="min-h-12 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 pl-10 font-mono tracking-widest focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder={t('magic_login_code_placeholder')}
                          disabled={isLoading}
                          required
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setMagicCodeSent(false);
                          setVerificationCode('');
                          setSuccess('');
                        }}
                        className="mt-2 min-h-11 text-sm font-semibold text-gray-500 hover:text-primary-700"
                      >
                        {t('magic_login_change_email')}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">{t('magic_login_description')}</p>
                  )}

                  {passwordPaused && (
                    <div id="password-login-wait" role="status" className="rounded-lg border border-secondary-200 bg-secondary-50 p-3 text-sm text-secondary-700">
                      <p>{t('login_wait_message')}</p>
                      <p className="mt-1 font-semibold tabular-nums" aria-live="off">{t('login_wait_remaining', { time: waitLabel })}</p>
                    </div>
                  )}

                  {error && !passwordPaused && (
                    <div className="p-3 bg-secondary-50 border border-secondary-200 text-secondary-700 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="p-3 bg-success-50 border border-success-200 text-success-700 rounded-lg text-sm">
                      {success}
                    </div>
                  )}

                  <button
                    type="submit"
                    className={`min-h-12 w-full rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${isLoading || passwordPaused
                        ? 'opacity-75 cursor-not-allowed'
                        : 'hover:bg-primary-700'
                      }`}
                    disabled={isLoading || passwordPaused}
                  >
                    {passwordPaused ? t('login_wait_button') : isLoading
                      ? t('login_loading')
                      : loginMethod === 'password'
                        ? t('login_button')
                        : magicCodeSent
                          ? t('magic_login_verify_button')
                          : t('magic_login_send_button')}
                  </button>

                  {loginMethod === 'password' && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          trackEvent('forgot_password_link_clicked');
                          setResetFlow('reset');
                          setMode('forgot');
                        }}
                        className="min-h-11 px-3 py-2 text-sm font-semibold text-gray-600 hover:text-primary-700"
                      >
                        {t('forgot_password_link')}
                      </button>
                    </div>
                  )}

                  <div className="border-t border-gray-200 pt-5 text-center">
                    <p className="text-sm text-gray-500">{t('register_prompt_no_account')}</p>
                    <button
                      type="button"
                      onClick={() => navigate(`/${locale}/register`)}
                      className="mt-3 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-5 py-3 text-sm font-bold text-primary-800 transition-colors hover:border-primary-300 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      {t('register_prompt_public_beta')}
                      <ArrowUpRight size={17} aria-hidden="true" />
                    </button>
                  </div>
                </form>
              </>
            )}

            {mode === 'forgot' && (
              <>
                <div className="flex items-center mb-4">
                  <button
                    type="button"
                    aria-label={t('back_to_login')}
                    onClick={() => {
                      trackEvent('forgot_password_back_clicked');
                      setResetFlow('reset');
                      setMode('login');
                    }}
                    className="mr-3 text-gray-600 hover:text-gray-800"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="text-2xl font-bold">{t('forgot_password_title')}</h2>
                </div>
                <p className="text-gray-600 mb-6">{t('forgot_password_description')}</p>
                <form key="forgot-form" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('forgot_password_email')}
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type="email"
                        id="forgot-email"
                        name="recovery_email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-300 bg-gray-50"
                        placeholder={t('forgot_password_email_placeholder')}
                        disabled={isLoading}
                        required
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 bg-secondary-50 border border-secondary-200 text-secondary-700 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="p-3 bg-success-50 border border-success-200 text-success-700 rounded-lg text-sm">
                      {success}
                    </div>
                  )}

                  <button
                    type="submit"
                    className={`w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-semibold shadow-sm transition-all ${isLoading
                        ? 'opacity-75 cursor-not-allowed'
                        : 'hover:bg-primary-700 hover:shadow-md'
                      }`}
                    disabled={isLoading}
                  >
                    {isLoading ? t('common_loading') : t('forgot_password_button')}
                  </button>
                </form>
              </>
            )}

            {mode === 'verify' && (
              <>
                <div className="flex items-center mb-4">
                  <button
                    type="button"
                    aria-label={t('forgot_password_title')}
                    onClick={() => {
                      trackEvent('verify_back_clicked');
                      setMode('forgot');
                    }}
                    className="mr-3 text-gray-600 hover:text-gray-800"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <h2 className="text-2xl font-bold">{resetFlow === 'activation' ? t('activation_verify_title') : t('verify_title')}</h2>
                </div>
                <p className="text-gray-600 mb-6">
                  {resetFlow === 'activation' ? t('activation_verify_description') : t('verify_description')}
                </p>
                <form key="verify-form" onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('forgot_password_email')}
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type="email"
                        id="verify-email"
                        name="verify_email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-300 bg-gray-50"
                        placeholder={t('forgot_password_email_placeholder')}
                        disabled={isLoading}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('verify_code')}
                    </label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type="text"
                        id="verification-code"
                        name="verification_code"
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-300 bg-gray-50"
                        placeholder={t('verify_code_placeholder')}
                        disabled={isLoading}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('reset_password_new')}
                    </label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="reset-new-password"
                        name="new_password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-10 pr-10 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-300 bg-gray-50"
                        placeholder={t('reset_password_new_placeholder')}
                        disabled={isLoading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-800">{t('password_requirements_title')}</p>
                    <div className="mt-2 space-y-2">
                      {passwordChecks.map((item) => (
                        <div key={item.id} className="flex items-center gap-3 text-sm">
                          <span
                            className={`flex h-5 w-5 items-center justify-center rounded-full border text-xs font-bold ${
                              item.valid
                                ? 'border-emerald-600 bg-emerald-600 text-white'
                                : 'border-gray-300 bg-white text-gray-400'
                            }`}
                          >
                            {item.valid ? <Check size={12} /> : null}
                          </span>
                          <span className={item.valid ? 'text-emerald-700' : 'text-gray-600'}>
                            {t(`password_requirement_${item.id}`)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('reset_password_confirm')}
                    </label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-2.5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="reset-confirm-password"
                        name="confirm_password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-300 bg-gray-50"
                        placeholder={t('reset_password_confirm_placeholder')}
                        disabled={isLoading}
                        required
                      />
                    </div>
                  </div>

                  {confirmPassword && (
                    <p className={`text-sm ${passwordsMatch ? 'text-emerald-700' : 'text-secondary-700'}`}>
                      {passwordsMatch ? t('password_match_ok') : t('reset_error_mismatch')}
                    </p>
                  )}

                  {error && (
                    <div className="p-3 bg-secondary-50 border border-secondary-200 text-secondary-700 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {success && (
                    <div className="p-3 bg-success-50 border border-success-200 text-success-700 rounded-lg text-sm">
                      {success}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !isStrongPassword(newPassword) || !passwordsMatch}
                    className={`w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-semibold shadow-sm transition-all ${
                      isLoading || !isStrongPassword(newPassword) || !passwordsMatch
                        ? 'cursor-not-allowed opacity-75'
                        : 'hover:bg-primary-700 hover:shadow-md'
                    }`}
                  >
                    {isLoading ? t('verify_loading') : (resetFlow === 'activation' ? t('activation_submit_button') : t('reset_password_button'))}
                  </button>
                </form>
              </>
            )}

            {mode === 'reset' && (
              <>
                <h2 className="text-2xl font-bold mb-4">{t('reset_password_title')}</h2>
                <div className="p-6 bg-white rounded-lg border border-gray-200 text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                    <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('password_reset_success_title')}</h3>
                  <p className="text-gray-600 mb-6">
                    {t('reset_success')}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent('reset_back_to_login_clicked');
                      setMode('login');
                    }}
                    className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    {t('back_to_login')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <SubtleFooter />
    </div>
  );
};

export default Login;
